import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spin, message, Input } from "antd";
import { platformApi } from "../../api";
import { ContextAPI } from "../../ContextAPI";
import { isDialableMobile, transferCall } from "../../utils/piopiy";

/**
 * Pick an AutoInn employee and blind-transfer the live call to their mobile.
 * Uses a clickable list (not AntD Select) so selection works above the call bar Modal.
 */
const RedirectCallModal = ({
  visible,
  onCancel,
  onTransferStarted,
  preferredExecutiveId = null,
}) => {
  const { loginCredintials } = useContext(ContextAPI);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState("");

  const currentUserId = loginCredintials?.employeeId || loginCredintials?.id;

  useEffect(() => {
    if (!visible) return;

    setSelectedUserId(null);
    setSearch("");
    setTransferring(false);
    setLoading(true);
    platformApi
      .get("/api/user")
      .then((res) => {
        const users = res?.data?.data?.users || res?.data?.users || [];
        setEmployees(Array.isArray(users) ? users : []);
      })
      .catch((err) => {
        console.error("Failed to load employees for redirect:", err);
        message.error("Failed to load employees");
        setEmployees([]);
      })
      .finally(() => setLoading(false));
  }, [visible]);

  const options = useMemo(() => {
    const dialable = employees
      .filter((u) => u && u.status !== false)
      .filter((u) => u.id !== currentUserId)
      // Skip users with no EmployeeProfile link (no name to show)
      .filter((u) => Boolean(u.profile?.employeeName))
      .map((u) => {
        const name = u.profile.employeeName;
        // Employee form "Personal Mobile Number" → User.phone2 (not phone)
        const phone = isDialableMobile(u.phone2)
          ? String(u.phone2).replace(/\D/g, "").slice(-10)
          : null;
        return {
          id: String(u.id),
          name,
          phone,
          label: phone ? `${name} — ${phone}` : `${name} — No personal mobile`,
        };
      })
      .filter((u) => u.phone);

    if (preferredExecutiveId) {
      const pref = String(preferredExecutiveId);
      dialable.sort((a, b) => {
        if (a.id === pref) return -1;
        if (b.id === pref) return 1;
        return a.name.localeCompare(b.name);
      });
    } else {
      dialable.sort((a, b) => a.name.localeCompare(b.name));
    }

    return dialable;
  }, [employees, currentUserId, preferredExecutiveId]);

  useEffect(() => {
    if (!visible || !preferredExecutiveId || !options.length) return;
    const pref = String(preferredExecutiveId);
    const preferred = options.find((o) => o.id === pref);
    if (preferred) {
      setSelectedUserId(preferred.id);
    }
  }, [visible, preferredExecutiveId, options]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        String(o.phone).includes(q) ||
        o.label.toLowerCase().includes(q)
    );
  }, [options, search]);

  const selected = options.find((o) => o.id === selectedUserId);

  const handleConfirm = () => {
    if (!selected?.phone) {
      message.error("Select an employee with a mobile number");
      return;
    }

    try {
      setTransferring(true);
      transferCall(selected.phone);
      if (onTransferStarted) {
        onTransferStarted(selected);
      }
    } catch (err) {
      message.error(err?.message || "Failed to redirect call");
      setTransferring(false);
    }
  };

  return (
    <Modal
      title="Redirect call to"
      visible={visible}
      onCancel={() => {
        if (transferring) return;
        onCancel && onCancel();
      }}
      onOk={handleConfirm}
      okText="Redirect"
      cancelText="Cancel"
      confirmLoading={transferring}
      okButtonProps={{ disabled: !selected?.phone || transferring }}
      destroyOnClose
      zIndex={11000}
      style={{ top: 72 }}
      maskClosable={!transferring}
      bodyStyle={{ paddingTop: 12 }}
    >
      <p style={{ marginBottom: 12, color: "#666", fontSize: 13 }}>
        Call will ring their personal mobile. Recording follows TeleCMI Team
        Record settings.
      </p>
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Input
            allowClear
            placeholder="Search employee by name or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={transferring}
            style={{ marginBottom: 8 }}
          />
          <div
            style={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
            }}
          >
            {filtered.map((emp) => {
              const isSelected = emp.id === selectedUserId;
              return (
                <div
                  key={emp.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (transferring) return;
                    setSelectedUserId(emp.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedUserId(emp.id);
                    }
                  }}
                  style={{
                    padding: "10px 12px",
                    cursor: transferring ? "not-allowed" : "pointer",
                    background: isSelected ? "#e6f7ff" : "#fff",
                    borderBottom: "1px solid #f0f0f0",
                    borderLeft: isSelected
                      ? "3px solid #1890ff"
                      : "3px solid transparent",
                    fontWeight: isSelected ? 600 : 400,
                    color: "#333",
                  }}
                >
                  {emp.label}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: 16, color: "#999", textAlign: "center" }}>
                No matching employees
              </div>
            )}
          </div>
          {selected && (
            <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13 }}>
              Selected: <strong>{selected.name}</strong> ({selected.phone})
            </p>
          )}
        </>
      )}
      {!loading && options.length === 0 && (
        <p style={{ marginTop: 12, color: "#999", fontSize: 12 }}>
          No employees with a dialable mobile number found.
        </p>
      )}
    </Modal>
  );
};

export default RedirectCallModal;
